import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {hovercardAttributesForActor} from '@github-ui/hovercards'
import {ProfileReference} from '@github-ui/profile-reference'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {Link} from '@primer/react'

import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import {GhostActor} from './GhostActor'
import type {IssueViewerTimelineEventActor} from './issue-viewer-view-query-types'
import styles from './row.module.css'

// RELAY-CLEANUP: widens nullable fields so still-Relay parent event components (e.g. AssignedEvent,
// ClosedEvent, etc.) can pass their Relay-typed actor data to plain-props TimelineRow/EventActor.
// Remove when all parent event components have been migrated off Relay.
export type EventActorFragment = RelayCompat<IssueViewerTimelineEventActor>

type EventActorProps = {
  actor: EventActorFragment | null
} & SharedProps

type SharedProps = {
  showAvatarOnly: boolean
  isAgent?: boolean
  displayName?: string
}

type EventActorBaseProps = {
  login?: string
  profileName?: string | null
  avatarUrl: string
  profileResourcePath?: string
  isCopilot?: boolean
  isUser?: boolean
} & SharedProps

export function EventActor({actor, ...props}: EventActorProps): React.ReactElement {
  if (!actor) return <GhostActor />

  const {login, name, avatarUrl, isCopilot, profileResourcePath, __typename, isAgent, displayName} = actor

  const isUser = ['User', 'Organization'].includes(__typename)

  return (
    <EventActorBase
      login={login}
      profileName={name}
      avatarUrl={avatarUrl}
      profileResourcePath={profileResourcePath ?? undefined}
      isCopilot={isCopilot}
      isUser={isUser}
      isAgent={isAgent}
      displayName={displayName}
      {...props}
    />
  )
}

function EventActorBase({
  login,
  profileName,
  avatarUrl,
  profileResourcePath,
  showAvatarOnly,
  isCopilot = false,
  isUser = true,
  isAgent = false,
  displayName = '',
}: EventActorBaseProps): React.ReactElement {
  if (login === VALUES.ghost.login) {
    return <span>{LABELS.repositoryOwner} </span>
  } else if (!login) {
    return <span>{VALUES.ghost.login} </span>
  }

  const useHovercard = isUser || isCopilot || isAgent
  const getActorDisplayName = () => {
    if (isCopilot) return VALUES.copilot.displayName
    if (isAgent && displayName) return displayName
    return login
  }
  const actorDisplayName = getActorDisplayName()

  return (
    <div className={styles.eventActorContainer}>
      <Link
        data-testid="actor-link"
        role="link"
        href={profileResourcePath}
        {...(useHovercard ? hovercardAttributesForActor(login, {isCopilot, isAgent}) : {})}
        className={styles.eventActorLink}
        muted
      >
        {isCopilot ? (
          <CopilotAvatar size={'small'} className={styles.alignSelfCenter} />
        ) : (
          <GitHubAvatar src={avatarUrl} size={16} className={styles.alignSelfCenter} />
        )}
        {!showAvatarOnly && (
          <span className={styles.eventProfileReference}>
            <ProfileReference login={actorDisplayName} profileName={profileName} isAgent={isAgent} />
          </span>
        )}
      </Link>
    </div>
  )
}
